import { format } from 'date-fns';
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import addonRevenueMonthlyService, {
  AddonRevenueMonthlyRow,
  AddonRevenueSource,
} from '../../services/addonRevenueMonthly.service';
import Header from '../layout/Header';
import DashboardNavigation from './components/DashboardNavigation';
import { MonthYearPicker } from './components/MonthYearPicker';
import { SimpleValueLineChart } from './components/SimpleValueLineChart';

const formatCurrency = (num: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
};

const getMonthValue = (period: string) => {
  // period is YYYY-MM-01
  return period?.slice(0, 7) || '';
};

const StripeAddonRevenueTool = () => {
  const qc = useQueryClient();

  const [source, setSource] = useState<AddonRevenueSource>('ncmic');
  const [month, setMonth] = useState<string>(format(new Date(), 'yyyy-MM'));
  const [amount, setAmount] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [enabled, setEnabled] = useState<boolean>(true);
  const [editing, setEditing] = useState<AddonRevenueMonthlyRow | null>(null);

  const listQuery = useQuery(['addon-revenue-monthly', source], () =>
    addonRevenueMonthlyService.list({ source, limit: 200 })
  );

  const seriesQuery = useQuery(['addon-revenue-monthly-series', source], () =>
    addonRevenueMonthlyService.series(source)
  );

  const createMutation = useMutation(
    () =>
      addonRevenueMonthlyService.create({
        source,
        period: month,
        amount: Number(amount),
        notes: notes || undefined,
        enabled,
      }),
    {
      onSuccess: async () => {
        setAmount('');
        setNotes('');
        setEnabled(true);
        await qc.invalidateQueries(['addon-revenue-monthly', source]);
        await qc.invalidateQueries(['addon-revenue-monthly-series', source]);
      },
    }
  );

  const updateMutation = useMutation(
    (row: AddonRevenueMonthlyRow) =>
      addonRevenueMonthlyService.update(row.id, {
        source: row.source,
        period: getMonthValue(row.period),
        amount: row.amount,
        notes: row.notes ?? undefined,
        enabled: !!row.enabled,
      }),
    {
      onSuccess: async () => {
        setEditing(null);
        await qc.invalidateQueries(['addon-revenue-monthly', source]);
        await qc.invalidateQueries(['addon-revenue-monthly-series', source]);
      },
    }
  );

  const deleteMutation = useMutation((id: number) => addonRevenueMonthlyService.del(id), {
    onSuccess: async () => {
      await qc.invalidateQueries(['addon-revenue-monthly', source]);
      await qc.invalidateQueries(['addon-revenue-monthly-series', source]);
    },
  });

  const rows = (listQuery.data?.data ?? []) as AddonRevenueMonthlyRow[];

  const normalizedRows = useMemo(() => {
    return [...rows].map(r => ({
      ...r,
      enabled: !!r.enabled,
    }));
  }, [rows]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <DashboardNavigation currentDashboardId="stripe" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-10">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 mt-6 mb-6">
          <div>
            <h1 className="text-2xl font-black text-gray-900">Add-on Revenue (Monthly)</h1>
            <p className="text-sm text-gray-600">
              Manual monthly totals for NCMIC + Practisync (used for dashboard metrics + charts).
            </p>
          </div>

          <div className="flex items-center gap-3">
            <label className="text-sm font-semibold text-gray-700">Source</label>
            <select
              className="px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm"
              value={source}
              onChange={e => {
                setSource(e.target.value as AddonRevenueSource);
                setEditing(null);
              }}
            >
              <option value="ncmic">NCMIC</option>
              <option value="practisync">Practisync</option>
            </select>
          </div>
        </div>

        {/* Chart */}
        <div className="mb-8">
          <SimpleValueLineChart
            title={`${source.toUpperCase()} Revenue Trend`}
            series={seriesQuery.data?.series ?? []}
            valueFormatter={formatCurrency}
          />
        </div>

        {/* Create / Edit */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow p-6 mb-8">
          <h2 className="text-lg font-bold text-gray-900 mb-4">
            {editing ? 'Edit Row' : 'Add New Month'}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="md:col-span-2">
              <div className="text-xs font-bold text-gray-600 uppercase mb-1">Month</div>
              <MonthYearPicker
                value={editing ? getMonthValue(editing.period) : month}
                onChange={next => {
                  if (editing) {
                    setEditing({ ...editing, period: `${next}-01` });
                  } else {
                    setMonth(next);
                  }
                }}
              />
            </div>

            <div>
              <div className="text-xs font-bold text-gray-600 uppercase mb-1">Amount</div>
              <input
                type="number"
                inputMode="decimal"
                className="w-full px-3 py-2 rounded-lg border border-gray-300"
                value={editing ? String(editing.amount ?? '') : amount}
                onChange={e =>
                  editing
                    ? setEditing({ ...editing, amount: Number(e.target.value) })
                    : setAmount(e.target.value)
                }
              />
            </div>

            <div className="md:col-span-2">
              <div className="text-xs font-bold text-gray-600 uppercase mb-1">Notes</div>
              <input
                type="text"
                className="w-full px-3 py-2 rounded-lg border border-gray-300"
                value={editing ? (editing.notes ?? '') : notes}
                onChange={e =>
                  editing
                    ? setEditing({ ...editing, notes: e.target.value })
                    : setNotes(e.target.value)
                }
                placeholder="Optional"
              />
            </div>

            <div className="flex items-end gap-3">
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                <input
                  type="checkbox"
                  checked={editing ? !!editing.enabled : enabled}
                  onChange={e =>
                    editing
                      ? setEditing({ ...editing, enabled: e.target.checked })
                      : setEnabled(e.target.checked)
                  }
                />
                Enabled
              </label>
            </div>
          </div>

          <div className="mt-5 flex items-center gap-3">
            {editing ? (
              <>
                <button
                  className="px-4 py-2 rounded-xl bg-emerald-600 text-white font-bold disabled:opacity-50"
                  disabled={updateMutation.isLoading}
                  onClick={() => editing && updateMutation.mutate(editing)}
                >
                  Save
                </button>
                <button
                  className="px-4 py-2 rounded-xl bg-gray-100 text-gray-800 font-bold"
                  onClick={() => setEditing(null)}
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                className="px-4 py-2 rounded-xl bg-emerald-600 text-white font-bold disabled:opacity-50"
                disabled={createMutation.isLoading || !month || !amount}
                onClick={() => createMutation.mutate()}
              >
                Add
              </button>
            )}
          </div>

          {(createMutation as any).error ? (
            <div className="mt-4 text-sm text-red-600">
              {(createMutation as any).error?.response?.data?.message || 'Failed to save'}
            </div>
          ) : null}
        </div>

        {/* List */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <h2 className="text-lg font-bold text-gray-900">Rows</h2>
            {listQuery.isLoading ? <div className="text-sm text-gray-500">Loading…</div> : null}
          </div>

          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase">
                  Month
                </th>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase">
                  Enabled
                </th>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase">
                  Notes
                </th>
                <th className="px-6 py-3 text-right text-xs font-bold text-gray-600 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {normalizedRows.map(r => (
                <tr key={r.id} className="hover:bg-gray-50">
                  <td className="px-6 py-3 text-sm font-semibold text-gray-900">
                    {getMonthValue(r.period)}
                  </td>
                  <td className="px-6 py-3 text-sm font-bold text-emerald-700">
                    {formatCurrency(Number(r.amount || 0))}
                  </td>
                  <td className="px-6 py-3 text-sm text-gray-700">{r.enabled ? 'Yes' : 'No'}</td>
                  <td className="px-6 py-3 text-sm text-gray-700">{r.notes ?? ''}</td>
                  <td className="px-6 py-3 text-right">
                    <div className="inline-flex items-center gap-2">
                      <button
                        className="px-3 py-1.5 rounded-lg bg-gray-100 text-gray-800 text-sm font-bold"
                        onClick={() => setEditing(r)}
                      >
                        Edit
                      </button>
                      <button
                        className="px-3 py-1.5 rounded-lg bg-red-50 text-red-700 text-sm font-bold"
                        onClick={() => {
                          if (window.confirm('Delete this row?')) {
                            deleteMutation.mutate(r.id);
                          }
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {!normalizedRows.length && !listQuery.isLoading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-10 text-center text-sm text-gray-500">
                    No rows yet.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default StripeAddonRevenueTool;
